import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNew } from 'src/utils/axios';

const ENDPOINT = '/admin/golis-transactions';

export const fetchGolisTransactions = createAsyncThunk(
  'golisBalance/fetch',
  async ({ queryParams }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstanceNew.get(`${ENDPOINT}?${queryParams.toString()}`);

      return data?.data || {};
    } catch (e) {
      return rejectWithValue(e?.response?.data || { message: 'Failed to fetch bank transactions' });
    }
  }
);

export const refreshGolisTransactions = fetchGolisTransactions;

export const markGolisTransactionOwner = createAsyncThunk(
  'golisBalance/markOwner',
  async ({ id, reviewNote }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstanceNew.patch(`${ENDPOINT}/${id}/owner`, {
        reviewNote: reviewNote || '',
      });

      return data?.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || { message: 'Failed to mark as owner' });
    }
  }
);

export const markGolisTransactionRefunded = createAsyncThunk(
  'golisBalance/markRefunded',
  async ({ id, refundTxId, reviewNote }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstanceNew.patch(`${ENDPOINT}/${id}/refunded`, {
        refundTxId: refundTxId || '',
        reviewNote: reviewNote || '',
      });

      return data?.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || { message: 'Failed to mark as refunded' });
    }
  }
);

function addTransactionIfNew(state, tx) {
  if (!tx?._id) return;

  const exists = state.transactions.some((item) => String(item._id) === String(tx._id));

  if (exists) return;

  state.transactions.unshift(tx);
}

function upsertTransaction(state, tx) {
  if (!tx?._id) return;

  const index = state.transactions.findIndex((item) => String(item._id) === String(tx._id));

  if (index >= 0) {
    state.transactions[index] = {
      ...state.transactions[index],
      ...tx,
    };
    return;
  }

  state.transactions.unshift(tx);
}

const initialState = {
  transactions: [],

  totalPages: 0,
  totalDocuments: 0,

  totalCountDebit: 0,
  totalCountCredit: 0,
  totalSumDebit: 0,
  totalSumCredit: 0,

  totalCountUnmatched: 0,
  totalCountMatched: 0,
  totalCountOwner: 0,
  totalCountRefunded: 0,

  lastUpdated: null,
  nextCursorDate: null,
  nextCursorId: null,

  isLoading: false,
  isUpdating: false,

  error: null,
  updateError: null,
};

const golisBalanceSlice = createSlice({
  name: 'golisBalance',
  initialState,
  reducers: {
    transactionsUpdated(state, action) {
      const { payload } = action;

      if (Array.isArray(payload)) {
        state.transactions = payload;
      } else if (Array.isArray(payload?.rows)) {
        state.transactions = payload.rows;
      } else if (payload?._id) {
        upsertTransaction(state, payload);
      }

      state.lastUpdated = Date.now();
    },

    fiatTransactionReceived(state, action) {
      const { payload } = action;

      if (Array.isArray(payload)) {
        payload.forEach((tx) => addTransactionIfNew(state, tx));
      } else if (Array.isArray(payload?.rows)) {
        payload.rows.forEach((tx) => addTransactionIfNew(state, tx));
      } else if (payload?._id) {
        addTransactionIfNew(state, payload);
      }

      state.lastUpdated = Date.now();
    },

    transactionStatusUpdated(state, action) {
      upsertTransaction(state, action.payload);
      state.lastUpdated = Date.now();
    },

    clearGolisTransactions(state) {
      state.transactions = [];
      state.totalPages = 0;
      state.totalDocuments = 0;

      state.totalCountDebit = 0;
      state.totalCountCredit = 0;
      state.totalSumDebit = 0;
      state.totalSumCredit = 0;

      state.totalCountUnmatched = 0;
      state.totalCountMatched = 0;
      state.totalCountOwner = 0;
      state.totalCountRefunded = 0;

      state.lastUpdated = null;
      state.nextCursorDate = null;
      state.nextCursorId = null;

      state.isLoading = false;
      state.isUpdating = false;

      state.error = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGolisTransactions.pending, (state) => {
        state.error = null;
        state.nextCursorDate = null;
        state.nextCursorId = null;
        state.isLoading = true;
      })

      .addCase(fetchGolisTransactions.fulfilled, (state, action) => {
        const payload = action.payload || {};

        state.isLoading = false;

        state.transactions = payload.items || [];

        state.totalPages = payload.totalPages || 0;
        state.totalDocuments = payload.totalDocuments || 0;

        state.totalCountDebit = payload.totalCountDebit || 0;
        state.totalCountCredit = payload.totalCountCredit || 0;
        state.totalSumDebit = payload.totalSumDebit || 0;
        state.totalSumCredit = payload.totalSumCredit || 0;

        state.totalCountUnmatched = payload.totalCountUnmatched || 0;
        state.totalCountMatched = payload.totalCountMatched || 0;
        state.totalCountOwner = payload.totalCountOwner || 0;
        state.totalCountRefunded = payload.totalCountRefunded || 0;

        state.nextCursorDate = payload.nextCursorDate || null;
        state.nextCursorId = payload.nextCursorId || null;

        state.lastUpdated = Date.now();
      })

      .addCase(fetchGolisTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed';
        state.nextCursorDate = null;
        state.nextCursorId = null;
      })

      .addCase(markGolisTransactionOwner.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })

      .addCase(markGolisTransactionOwner.fulfilled, (state, action) => {
        state.isUpdating = false;
        upsertTransaction(state, action.payload);
        state.lastUpdated = Date.now();
      })

      .addCase(markGolisTransactionOwner.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload || action.error?.message || 'Failed to mark as owner';
      })

      .addCase(markGolisTransactionRefunded.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })

      .addCase(markGolisTransactionRefunded.fulfilled, (state, action) => {
        state.isUpdating = false;
        upsertTransaction(state, action.payload);
        state.lastUpdated = Date.now();
      })

      .addCase(markGolisTransactionRefunded.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload || action.error?.message || 'Failed to mark as refunded';
      });
  },
});

export const {
  transactionsUpdated,
  transactionStatusUpdated,
  clearGolisTransactions,
  fiatTransactionReceived,
} = golisBalanceSlice.actions;

export default golisBalanceSlice.reducer;
